import { createTheme } from '@mui/material/styles';

const createAppTheme = (mode = 'dark') => createTheme({
  palette: {
    mode,
    primary: {
      main: '#2196f3', // Blue
      light: '#64b5f6',
      dark: '#1976d2',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#ff9800', // Orange
      light: '#ffb74d',
      dark: '#f57c00',
      contrastText: mode === 'dark' ? '#ffffff' : '#000000',
    },
    background: {
      default: mode === 'dark' ? '#0a0e27' : '#f5f5f5',
      paper: mode === 'dark' ? '#1e2139' : '#ffffff',
    },
    text: {
      primary: mode === 'dark' ? '#ffffff' : '#000000',
      secondary: mode === 'dark' ? '#b0bec5' : '#666666',
    },
    success: {
      main: '#4caf50',
      light: '#81c784',
      dark: '#388e3c',
    },
    error: {
      main: '#f44336',
      light: '#e57373',
      dark: '#d32f2f',
    },
    warning: {
      main: '#ff9800',
      light: '#ffb74d',
      dark: '#f57c00',
    },
    info: {
      main: '#2196f3',
      light: '#64b5f6',
      dark: '#1976d2',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      background: 'linear-gradient(45deg, #2196f3, #64b5f6)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h5: {
      fontWeight: 500,
      fontSize: '1.25rem',
    },
    h6: {
      fontWeight: 500,
      fontSize: '1rem',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 25,
          textTransform: 'none',
          fontWeight: 600,
          padding: '10px 24px',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)',
          },
        },
        contained: {
          background: 'linear-gradient(45deg, #2196f3, #64b5f6)',
          color: '#ffffff',
          '&:hover': {
            background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
          },
        },
        outlined: {
          borderColor: '#2196f3',
          color: '#2196f3',
          '&:hover': {
            borderColor: '#64b5f6',
            backgroundColor: 'rgba(33, 150, 243, 0.1)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, #1e2139 0%, #252a4a 100%)',
          border: '1px solid #2196f3',
          borderRadius: 15,
          boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 12px 35px rgba(33, 150, 243, 0.2)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, #1e2139 0%, #252a4a 100%)',
          border: '1px solid rgba(33, 150, 243, 0.3)',
          borderRadius: 15,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            '& fieldset': {
              borderColor: 'rgba(33, 150, 243, 0.5)',
            },
            '&:hover fieldset': {
              borderColor: '#2196f3',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#2196f3',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#b0bec5',
            '&.Mui-focused': {
              color: '#2196f3',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 15,
          fontWeight: 500,
        },
        colorPrimary: {
          background: 'linear-gradient(45deg, #2196f3, #64b5f6)',
          color: '#ffffff',
        },
        colorSecondary: {
          background: 'linear-gradient(45deg, #ff9800, #ffb74d)',
          color: '#000000',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(90deg, #1565c0 0%, #2196f3 50%, #1565c0 100%)',
          borderBottom: '3px solid #64b5f6',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          background: 'linear-gradient(135deg, #1e2139 0%, #252a4a 100%)',
          border: '2px solid #2196f3',
          borderRadius: 20,
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 15,
          border: '1px solid rgba(33, 150, 243, 0.3)',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: 'rgba(33, 150, 243, 0.1)',
            fontWeight: 600,
            fontSize: '1rem',
            color: '#2196f3',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:nth-of-type(odd)': {
            backgroundColor: 'rgba(33, 150, 243, 0.05)',
          },
          '&:hover': {
            backgroundColor: 'rgba(33, 150, 243, 0.1)',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(33, 150, 243, 0.2)',
          padding: '16px',
        },
      },
    },
  },
  shape: {
    borderRadius: 15,
  },
});

export default createAppTheme;
